import { forwardRef, useCallback, useEffect, useState, type ComponentType } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { ViewTabs } from '@/components/ViewTabs';
import { notifyError } from '@/lib/notify';
import { isEmpty } from '../../../../ui/utils';
import { exportAllGrids } from '../../../../utils/exportAllGrids';
import { Coverages as CoveragesTs } from '../../../QuotePage/content/QuoteGrids/Coverages';
import { MiscValues as MiscValuesJs } from '../../../QuotePage/content/QuoteGrids/MiscValues';
import { PremiumCalculation as PremiumCalculationJs } from '../../../MCEPage/content/PremiumCalculation';
import { MCEFees as MCEFeesJs } from '../../../MCEPage/content/MCEFees';
import { LocationDetails as LocationDetailsJs } from '../../../MCEPage/content/LocationDetails';
import { Endorsements as EndorsementsJs } from '../../Endorsements';
import { MiscPolicyDetails as MiscPolicyDetailsJs } from '../../MiscPolicyDetails';
import { MonthlyReporting as MonthlyReportingJs } from '../MonthlyReporting';
import { NamedInsured as NamedInsuredJs } from '../../NamedInsured';
import { HighValueVehicles as HighValueVehiclesJs } from '../../HighValueVehicles';
import { TemporaryLocations as TemporaryLocationsJs } from '../../TemporaryLocations/TemporaryLocations';
import { InventoryValue as InventoryValueJs } from '../../InventoryValue';
import { LossPayee as LossPayeeJs } from '../../LossPayee/LossPayee';
import { FalsePretense as FalsePretenseJs } from '../../FalsePretense';
import { PolicyGeneration as PolicyGenerationJs } from '../../PolicyGeneration';
import { Attachments as AttachmentsJs } from '../../../Attachments/Attachments';
import { usePolicyPageContext as usePolicyPageContextJs } from '../../context/PolicyPageContext';

/* The tab views take the page's props through, and some are still JavaScript (the MCE page's own
   tabs stay legacy, D9); each is used here with the props this file passes. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: the tab view components
type View = ComponentType<any>;
const as = (component: unknown) => component as View;
const Coverages = as(CoveragesTs);
const MiscValues = as(MiscValuesJs);
const PremiumCalculation = as(PremiumCalculationJs);
const MCEFees = as(MCEFeesJs);
const LocationDetails = as(LocationDetailsJs);
const Endorsements = as(EndorsementsJs);
const MiscPolicyDetails = as(MiscPolicyDetailsJs);
const MonthlyReporting = as(MonthlyReportingJs);
const NamedInsured = as(NamedInsuredJs);
const HighValueVehicles = as(HighValueVehiclesJs);
const TemporaryLocations = as(TemporaryLocationsJs);
const InventoryValue = as(InventoryValueJs);
const LossPayee = as(LossPayeeJs);
const FalsePretense = as(FalsePretenseJs);
const PolicyGeneration = as(PolicyGenerationJs);
const Attachments = as(AttachmentsJs);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: rows the grids hold
type Row = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: an AG Grid API handed up by a grid
type GridRef = { api: any } | null;
type PolicyPageState = {
  namedInsureds: Row[];
  setNamedInsureds: (rows: Row[]) => void;
  highValueVehicles: Row[];
  setHighValueVehicles: (rows: Row[]) => void;
  temporaryLocations: Row[];
  setTemporaryLocations: (rows: Row[]) => void;
  lossPayee: Row[];
  setLossPayee: (rows: Row[]) => void;
  endorsements?: Row[];
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;

export const policyViewTabs = {
  COVERAGES: 'COVERAGES',
  MISC_VALUES: 'MISC_VALUES',
  MCES: 'MCES',
  MONTHLY_REPORTING: 'MONTHLY_REPORTING',
  PREMIUM_CALCULATION: 'PREMIUM_CALCULATION',
  MCE_FEES: 'MCE_FEES',
  LOCATION_DETAILS: 'LOCATION_DETAILS',
  ENDORSEMENTS: 'ENDORSEMENTS',
  NAMED_INSURED: 'NAMED_INSURED',
  HIGH_VALUE_VEHICLES: 'HIGH_VALUE_VEHICLES',
  TEMPORARY_LOCATIONS: 'TEMPORARY_LOCATIONS',
  FALSE_PRETENSE: 'FALSE_PRETENSE',
  LOSS_PAYEE: 'LOSS_PAYEE',
  POLICY_GENERATION: 'POLICY_GENERATION',
  ATTACHMENTS: 'ATTACHMENTS',
  INVENTORY_VALUE: 'INVENTORY_VALUE',
};

const LABELS: Record<string, string> = {
  MCE_FEES: 'Fees',
  COVERAGES: 'Coverages',
  MCES: 'Misc. Change Endorsements(MCEs)',
  MONTHLY_REPORTING: 'Monthly Reporting',
  PREMIUM_CALCULATION: 'Premium Calculation',
  ENDORSEMENTS: 'Endorsements',
  NAMED_INSURED: 'Named Insured',
  HIGH_VALUE_VEHICLES: 'High Value Vehicles',
  TEMPORARY_LOCATIONS: 'Temporary Locations',
  FALSE_PRETENSE: 'False Pretense',
  LOSS_PAYEE: 'Loss Payee',
  POLICY_GENERATION: 'Policy Generation',
  ATTACHMENTS: 'Attachments',
  INVENTORY_VALUE: 'Inventory Value',
  LOCATION_DETAILS: 'Location Details',
};

const getLabel = (name: string, label: string) => LABELS[name] ?? label;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: the policy and MCE pages pass their own props through
type PolicyTabsProps = Record<string, any> & { policyTabsList: Record<string, string>; data: Row };

/** The policy (and MCE) page's tab bar and its panels. Every panel stays mounted except Attachments. */
export const PolicyTabs = forwardRef<unknown, PolicyTabsProps>((props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [gridParams, setGridParams] = useState({});
  const [selectedFields, setSelectedFields] = useState({});
  const [exportHandlers, setExportHandlers] = useState<Record<string, unknown>>({});
  const [quoteData, setQuoteData] = useState(props.data);
  const { setMCEDetails, MCEDetails, setDeletedLocations, setPremiumData } = props;
  const {
    namedInsureds,
    setNamedInsureds,
    highValueVehicles,
    setHighValueVehicles,
    temporaryLocations,
    setTemporaryLocations,
    lossPayee,
    setLossPayee,
    endorsements = [],
  } = usePolicyPageContext();
  const [enableDelete, setEnableDelete] = useState(false);

  const policyTabsKeys = Object.keys(props?.policyTabsList || policyViewTabs);
  const [gridReferences, setGridReferences] = useState<Record<string, GridRef>>({
    COVERAGES: null,
    MCE_FEES: null,
    MONTHLY_REPORTING: null,
    LOCATION_DETAILS: null,
    MCES: null,
    ENDORSEMENTS: null,
    HIGH_VALUE_VEHICLES: null,
    TEMPORARY_LOCATIONS: null,
    FALSE_PRETENSE: null,
    LOSS_PAYEE: null,
    INVENTORY_VALUE: null,
  });

  const setGridReferenceForTab = ({ tabName, ref }: { tabName: string; ref: GridRef }) => {
    setGridReferences((prevRefs) => ({ ...prevRefs, [tabName]: ref }));
  };

  const selectedRowsOf = (tab: string): Row[] => gridReferences[tab]?.api.getSelectedRows();

  const handleDeleteMCELocation = async () => {
    try {
      const { premiumData } = props;
      const selected = selectedRowsOf('PREMIUM_CALCULATION');
      if (!selected || selected.length === 0) {
        notifyError('Please select locations to delete.');
        return;
      }
      if (!premiumData || premiumData.length === 0) {
        console.warn('No premiumData available to update.');
        return;
      }
      setDeletedLocations(selected);
      const updatedPremiumData = premiumData.map((row: Row) => {
        const isSelected = selected.some((selectedRow) => selectedRow.locationId === row.locationId);
        return isSelected ? { ...row, isRowDeleted: true, avg_declared_inventory: 0 } : row;
      });
      setPremiumData(updatedPremiumData);
    } catch (error) {
      console.error('Failed to update premium data:', error);
      notifyError('An error occurred while updating the premium data. Please try again.');
    }
  };

  const removeSelected = (tab: string, rows: Row[], setRows: (rows: Row[]) => void, emptyMessage: string, noRowsLog: string, key: string) => {
    const selected = selectedRowsOf(tab);
    if (!selected || selected.length === 0) {
      notifyError(emptyMessage);
      return;
    }
    if (!rows || rows.length === 0) {
      console.warn(noRowsLog);
      return;
    }
    setRows(rows.filter((row) => !selected.some((s) => s[key] === row[key])));
    setEnableDelete(false);
  };

  const handleDeleteInsureds = async () =>
    removeSelected(
      'NAMED_INSURED',
      namedInsureds,
      setNamedInsureds,
      'Please select locations to delete.',
      'No namedInsured available to update.',
      'id'
    );
  const handleDeleteLossPayee = async () =>
    removeSelected('LOSS_PAYEE', lossPayee, setLossPayee, 'Please select data to delete.', 'No lossPayee available to update.', 'id');
  const handleDeleteHVV = async () =>
    removeSelected(
      'HIGH_VALUE_VEHICLES',
      highValueVehicles,
      setHighValueVehicles,
      'Please select locations to delete.',
      'No Rows available to update.',
      'id'
    );
  const handleDeleteTemporaryLocations = async () =>
    removeSelected(
      'TEMPORARY_LOCATIONS',
      temporaryLocations,
      setTemporaryLocations,
      'Please select locations to delete.',
      'No Rows available to update.',
      'locationNumber'
    );

  const handleDeleteFee = async (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.();
    const fields = selectedRowsOf('MCE_FEES');
    if (isEmpty(fields) || !fields) return;
    const result = MCEDetails?.fees.filter((item: Row) => !fields.find((selectedItem) => selectedItem.id === item.id));
    setMCEDetails((pre: Row) => ({ ...pre, fees: result }));
  };

  const setActiveExportFunction = (exportFn: unknown, gridType: string) => {
    setExportHandlers((prevHandlers) => ({ ...prevHandlers, [gridType]: exportFn }));
  };

  const setGridReference = (params: object) => setGridParams(params);

  const onSelectionChanged = useCallback((context: GridRef) => {
    if (context) {
      const fields = context.api.getSelectedRows();
      if (fields.length) setEnableDelete(true);
    }
  }, []);

  useEffect(() => {
    setQuoteData(props.data);
  }, [props.data]);

  const policyTabsList = Object.keys(props.policyTabsList);
  const [viewTab, setViewTab] = useState(policyTabsList[0]);
  useEffect(() => {
    setModalOpen(false);
  }, [viewTab]);

  const has = (tab: string) => policyTabsList.includes(tab);
  const panel = (tab: string, content: JSX.Element) =>
    has(tab) ? (
      <TabsContent key={tab} value={tab} keepMounted={tab !== policyViewTabs.ATTACHMENTS}>
        {content}
      </TabsContent>
    ) : null;

  const {
    data,
    productCoverages,
    MCEsData,
    monthlyReportingData,
    showStatementsListModal,
    setShowStatementsListModal,
    importModal,
    setImportModal,
    handleAddLocation,
    allowDeleteCoverage = false,
    shouldShowWarning,
    setShouldShowWarning,
  } = props;

  return (
    <div className="w-full">
      <ViewTabs
        getLabel={getLabel}
        tabs={policyTabsKeys}
        onTabChanged={setViewTab}
        gridReference={gridParams}
        selectedFields={selectedFields as Row[]}
        setSelectedFields={setSelectedFields}
        setQuoteData={setQuoteData}
        quotesView={false}
        quoteData={quoteData}
        handleDeleteFee={handleDeleteFee}
        handleDeleteMCELocation={handleDeleteMCELocation}
        handleDeleteInsureds={handleDeleteInsureds}
        handleDeleteLossPayee={handleDeleteLossPayee}
        handleDeleteHVV={handleDeleteHVV}
        handleDeleteTemporaryLocations={handleDeleteTemporaryLocations}
        onExport={() => exportAllGrids({ gridReferences, exportHandlers, workbookName: 'policy' })}
        enableDelete={enableDelete}
        hasIVE={endorsements?.some((e) => e?.endorsementType === 'invEndorsement')}
        barClassName="flex justify-between border-b border-grid-border bg-white pr-[18px] pl-2.5"
        {...props}
      >
        {panel(
          policyViewTabs.COVERAGES,
          <Coverages
            quotes={quoteData}
            productCoverages={productCoverages}
            onSelectionChanged={onSelectionChanged}
            modalOpen={modalOpen}
            setQuoteData={setQuoteData}
            setModalOpen={setModalOpen}
            importModal={importModal}
            gridReference={gridReferences?.COVERAGES}
            setImportModal={setImportModal}
            setExportHandler={(exportFn: unknown) => setActiveExportFunction(exportFn, policyViewTabs.COVERAGES)}
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'COVERAGES', ref })}
            handleAddLocation={handleAddLocation}
            allowDeleteCoverage={allowDeleteCoverage}
            shouldShowWarning={shouldShowWarning}
            setShouldShowWarning={setShouldShowWarning}
          />
        )}
        {panel(policyViewTabs.MCES, <MiscPolicyDetails setGridReference={setGridReference} policy={data} MCEsData={MCEsData} />)}
        {panel(policyViewTabs.MISC_VALUES, <MiscValues setGridReference={setGridReference} />)}
        {panel(
          policyViewTabs.MONTHLY_REPORTING,
          <MonthlyReporting
            setGridReference={setGridReference}
            policy={data}
            monthlyReportingData={monthlyReportingData}
            showStatementsListModal={showStatementsListModal}
            setShowStatementsListModal={setShowStatementsListModal}
          />
        )}
        {panel(
          policyViewTabs.PREMIUM_CALCULATION,
          <PremiumCalculation
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'PREMIUM_CALCULATION', ref })}
            policy={data}
            gridReference={gridReferences?.PREMIUM_CALCULATION}
            monthlyReportingData={monthlyReportingData}
            onSelectionChanged={onSelectionChanged}
          />
        )}
        {panel(
          policyViewTabs.MCE_FEES,
          <MCEFees
            policy={data}
            gridReference={gridReferences?.MCE_FEES}
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'MCE_FEES', ref })}
            onSelectionChanged={onSelectionChanged}
          />
        )}
        {panel(
          policyViewTabs.LOCATION_DETAILS,
          <LocationDetails
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'LOCATION_DETAILS', ref })}
            policy={data}
            gridReference={gridReferences?.LOCATION_DETAILS}
            onSelectionChanged={onSelectionChanged}
          />
        )}
        {panel(
          policyViewTabs.ENDORSEMENTS,
          <Endorsements setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'ENDORSEMENTS', ref })} policy={data} />
        )}
        {panel(
          policyViewTabs.NAMED_INSURED,
          <NamedInsured
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'NAMED_INSURED', ref })}
            policy={data}
            gridReference={gridReferences?.NAMED_INSURED}
            onSelectionChanged={onSelectionChanged}
          />
        )}
        {panel(
          policyViewTabs.HIGH_VALUE_VEHICLES,
          <HighValueVehicles
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'HIGH_VALUE_VEHICLES', ref })}
            policy={data}
            gridReference={gridReferences?.HIGH_VALUE_VEHICLES}
            onSelectionChanged={onSelectionChanged}
          />
        )}
        {panel(
          policyViewTabs.TEMPORARY_LOCATIONS,
          <TemporaryLocations
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'TEMPORARY_LOCATIONS', ref })}
            policy={data}
            gridReference={gridReferences?.TEMPORARY_LOCATIONS}
            onSelectionChanged={onSelectionChanged}
          />
        )}
        {panel(
          policyViewTabs.FALSE_PRETENSE,
          <FalsePretense
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'FALSE_PRETENSE', ref })}
            policy={data}
            gridReference={gridReferences?.FALSE_PRETENSE}
          />
        )}
        {panel(
          policyViewTabs.LOSS_PAYEE,
          <LossPayee
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'LOSS_PAYEE', ref })}
            policy={data}
            gridReference={gridReferences?.LOSS_PAYEE}
            onSelectionChanged={onSelectionChanged}
          />
        )}
        {panel(policyViewTabs.POLICY_GENERATION, <PolicyGeneration />)}
        {panel(
          policyViewTabs.ATTACHMENTS,
          <Attachments
            entity={data}
            entityName={'policy'}
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'ATTACHMENTS', ref })}
            gridReference={gridReferences?.ATTACHMENTS}
          />
        )}
        {panel(
          policyViewTabs.INVENTORY_VALUE,
          <InventoryValue
            setGridReference={(ref: GridRef) => setGridReferenceForTab({ tabName: 'INVENTORY_VALUE', ref })}
            policy={data}
            MCEsData={MCEsData}
            gridReference={gridReferences?.INVENTORY_VALUE}
          />
        )}
      </ViewTabs>
    </div>
  );
});
PolicyTabs.displayName = 'PolicyTabs';
